import React from 'react';
import 'twin.macro';
import { HomeIcon } from 'containers/Sequences/icons/HomeIcon';
import { getLinkOfFolderNode } from 'containers/Sequences/urls';
import { FolderSequenceIcon } from 'containers/Sequences/FolderSequenceIcon';
import { useLocation, useNavigate } from 'react-router-dom';
import { FolderNode } from 'types/sequences';
import { useTranslation } from 'react-i18next';
import {
  Breadcrumb2,
  BreadcrumbItem,
} from '@teetsh/app/src/components/Breadcrumb2';

interface Props {
  className?: string;
  onChange?: (v: FolderNode) => any;
  folder?: FolderNode;
  ancestors?: FolderNode[];
  icon?: any;
  mobile?: boolean;
}

export function Breadcrumb(props: Props) {
  const navigate = useNavigate();
  const { ancestors, folder, mobile } = props;
  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);
  const isNewFolder = urlSearchParams.get('new') === 'true';
  const { t } = useTranslation(['sequence']);

  // We want to partition: show the last N items, hide the rest
  // The original partitionLastN logic: show last 1 ancestor + current folder
  const allAncestors = ancestors || [];
  const numToShow = 1; // Show last 1 ancestor (plus current folder if exists)
  const hiddenAncestors =
    allAncestors.length > numToShow ? allAncestors.slice(0, -numToShow) : [];
  const visibleAncestors =
    allAncestors.length > numToShow
      ? allAncestors.slice(-numToShow)
      : allAncestors;

  const breadcrumbItems: BreadcrumbItem[] = [
    ...visibleAncestors.map(ancestor => ({
      id: ancestor.id,
      text: ancestor.title,
      to: getLinkOfFolderNode(ancestor, ancestors || []),
      icon: (
        <FolderSequenceIcon
          type={ancestor.type}
          tw="lg:mr-1 -my-2 shrink-0 size-7"
        />
      ),
      hideTextOnMobile: true,
    })),
    ...(folder
      ? [
          {
            id: folder.id,
            text: folder.title,
            icon: (
              <FolderSequenceIcon
                type={folder.type}
                tw="mr-1 -my-2 shrink-0 size-7"
              />
            ),
            isActive: true,
            editable: folder.type === 'folder' && !mobile,
            editAutoFocus: isNewFolder,
            onEdit: (v: string) =>
              props.onChange &&
              props.onChange({
                ...folder,
                title: v,
              }),
            hideTextOnMobile: mobile,
          },
        ]
      : []),
  ];

  // Build overflow menu items only when there are hidden ancestors
  const overflowMenuItems: BreadcrumbItem[] =
    hiddenAncestors.length > 0
      ? [
          {
            id: 'home',
            text: 'Accueil',
            onClick: () => navigate('/sequences'),
            icon: (() => <HomeIcon tw="size-6 mr-2 shrink-0" />) as any,
          },
          ...hiddenAncestors.map(ancestor => ({
            id: ancestor.id,
            text: ancestor.title,
            onClick: () => navigate(getLinkOfFolderNode(ancestor, [])),
            icon: (() => (
              <FolderSequenceIcon type={ancestor.type} tw="mr-2 size-6" />
            )) as any,
          })),
        ]
      : [];

  // Show home icon when no items are hidden, show ellipsis when items are hidden
  const showHomeIcon = hiddenAncestors.length === 0;

  return (
    <Breadcrumb2
      className={props.className}
      items={breadcrumbItems}
      tw="py-2"
      home={
        showHomeIcon
          ? {
              to: '/sequences',
              icon: (() => (
                <HomeIcon tw="-my-2 shrink-0 size-7" aria-hidden="true" />
              )) as any,
              text: t('sequence:summary'),
              ariaLabel: 'Accueil séquences',
            }
          : undefined
      }
      overflowMenuItems={overflowMenuItems}
    />
  );
}
